import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TestTube, Home as HomeIcon, Building2, Clock, Droplet, ChevronRight, ArrowLeft, CheckCircle2, CalendarDays, Search, X, IndianRupee, Plus, Minus } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { OtpModal, loadRazorpay } from "../components/OtpModal";
import { UpiQrPayModal } from "../components/UpiQrPayModal";
import { toast } from "sonner";

const SLOT_TIMES = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00",
                    "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00"];

export default function Pathology() {
  const { lang, user } = useApp();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cfg, setCfg] = useState({ home_collection_fee: 150, platform_fee: 49 });
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]); // list of test_id
  const [step, setStep] = useState(1); // 1: select, 2: patient+slot+collection, 3: confirmation
  const [collection, setCollection] = useState("lab"); // lab | home
  const [collectionAddr, setCollectionAddr] = useState("");
  const [slotDate, setSlotDate] = useState(new Date(Date.now() + 24*3600*1000).toISOString().slice(0,10));
  const [slotTime, setSlotTime] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [patient, setPatient] = useState({ name: user?.name || "", age: 30, gender: "male", phone: "", email: user?.email || "", address: "" });
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponErr, setCouponErr] = useState("");

  useEffect(() => {
    (async () => {
      const [t, c, cf] = await Promise.all([
        api.get("/pathology/tests"),
        api.get("/pathology/categories"),
        api.get("/pathology/config"),
      ]);
      setTests(t.data);
      setCategories(["All", ...c.data]);
      setCfg(cf.data);
    })();
  }, []);

  const filtered = useMemo(() => {
    return tests.filter(t =>
      (cat === "All" || t.category === cat) &&
      (!search || t.name.toLowerCase().includes(search.toLowerCase()))
    );
  }, [tests, cat, search]);

  const cartTests = useMemo(() => cart.map(id => tests.find(t => t.test_id === id)).filter(Boolean), [cart, tests]);
  const testsSubtotal = cartTests.reduce((s, t) => s + t.price, 0);
  const collectionFee = collection === "home" ? cfg.home_collection_fee : 0;
  const subtotal = testsSubtotal + cfg.platform_fee + collectionFee;
  const total = Math.max(0, subtotal - couponDiscount);
  const inCart = (id) => cart.includes(id);
  const toggleCart = (id) => setCart(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const anyFasting = cartTests.some(t => t.fasting_required);

  const applyCoupon = async () => {
    setCouponErr("");
    if (!couponCode.trim()) return;
    try {
      const r = await api.post("/coupons/validate", { code: couponCode.trim(), amount: subtotal, service: "pathology" });
      setCouponDiscount(r.data.discount);
      toast.success(`Coupon applied: −₹${r.data.discount}`);
    } catch (e) {
      setCouponDiscount(0);
      setCouponErr(e?.response?.data?.detail || "Invalid coupon");
    }
  };

  const canProceed2 = () => {
    if (cart.length === 0) return false;
    if (!slotDate || !slotTime) return false;
    if (!patient.name || !patient.age || !/^\d{10}$/.test((patient.phone || "").replace(/\D/g, "").slice(-10) || "")) return false;
    if (collection === "home" && (collectionAddr || "").trim().length < 5) return false;
    return true;
  };

  const placeBooking = async (tokenOverride) => {
    if (!user) { navigate("/login"); return; }
    const tok = tokenOverride || otpToken;
    if (!tok) { setOtpOpen(true); return; }
    setPlacing(true);
    try {
      const payload = {
        test_ids: cart,
        slot_date: slotDate, slot_time: slotTime,
        collection_method: collection,
        collection_address: collection === "home" ? collectionAddr : null,
        doctor_name: doctorName || null,
        patient,
        coupon_code: couponDiscount > 0 ? couponCode.trim() : null,
        otp_token: tok,
      };
      const r = await api.post("/pathology/bookings", payload);
      setConfirmed(r.data);
      const o = await api.post("/payments/create-order", { booking_id: r.data.booking_id, type: "booking" });
      setOrderInfo(o.data);
      // Show confirmation screen immediately (payment_status still "pending" in mock mode until admin/webhook confirms)
      setStep(3);
      if (o.data.mock) {
        setPayOpen(true);
      } else {
        await openRazorpay(o.data, r.data.booking_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    }
    setPlacing(false);
  };

  const openRazorpay = async (order, bookingId) => {
    const ok = await loadRazorpay();
    if (!ok) { toast.error("Payment SDK failed to load"); return; }
    const rzp = new window.Razorpay({
      key: order.key_id, amount: order.amount, currency: order.currency,
      order_id: order.order_id, name: order.merchant_name,
      description: `Pathology ${bookingId}`,
      prefill: order.prefill,
      theme: { color: "#a21caf" },
      handler: async (resp) => {
        try {
          await api.post("/payments/verify", {
            booking_id: bookingId, type: "booking",
            order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          setStep(3);
        } catch { navigate("/payment/failed"); }
      },
      modal: { ondismiss: () => toast.info("Payment cancelled") },
    });
    rzp.open();
  };

  return (
    <div className={lang === "hi" ? "lang-hi" : ""}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/")} data-testid="pathology-back" className="text-sm text-slate-500 hover:text-fuchsia-600 inline-flex items-center gap-1">
          <ArrowLeft size={16}/> Home
        </button>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-fuchsia-100 text-fuchsia-600 flex items-center justify-center"><TestTube size={22}/></div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">{lang==="hi"?"पैथोलॉजी टेस्ट बुकिंग":"Pathology Test Booking"}</h1>
            <p className="text-slate-600 text-sm">{lang==="hi"?"CBC, थायरॉइड, शुगर, विटामिन आदि · होम कलेक्शन उपलब्ध":"CBC, Thyroid, Sugar, Vitamins and more · Home collection available"}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-6 flex items-center gap-3 text-sm">
          {[1,2,3].map(s => (
            <div key={s} className={`flex items-center gap-2 ${s <= step ? "text-fuchsia-600" : "text-slate-400"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${s <= step ? "bg-fuchsia-600 text-white" : "bg-slate-100"}`}>{s}</div>
              <span className="hidden sm:inline">{s === 1 ? (lang==="hi"?"टेस्ट चुनें":"Select Tests") : s === 2 ? (lang==="hi"?"विवरण & भुगतान":"Details & Pay") : (lang==="hi"?"पुष्टि":"Confirmation")}</span>
              {s < 3 && <ChevronRight size={14}/>}
            </div>
          ))}
        </div>

        {/* Step 1 — test selection */}
        {step === 1 && (
          <div className="mt-6 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-2xl card-shadow p-4 flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder={lang==="hi"?"टेस्ट खोजें (जैसे CBC, Thyroid)":"Search tests (e.g. CBC, Thyroid)"}
                    data-testid="pathology-search"
                    className="w-full rounded-full bg-slate-50 border border-slate-200 pl-10 pr-4 py-2 text-sm outline-none focus:border-fuchsia-500"/>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {categories.map(c => (
                  <button key={c} onClick={() => setCat(c)}
                    data-testid={`pathology-cat-${c}`}
                    className={`px-3 py-1 text-xs rounded-full border ${cat === c ? "bg-fuchsia-600 text-white border-fuchsia-600" : "bg-white border-slate-200 hover:border-fuchsia-400"}`}>{c}</button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {filtered.map(t => (
                  <div key={t.test_id} data-testid={`pathology-card-${t.test_id}`}
                    className={`bg-white rounded-2xl card-shadow p-4 border-2 transition ${inCart(t.test_id) ? "border-fuchsia-500" : "border-transparent"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="chip bg-fuchsia-50 text-fuchsia-700 text-[10px]">{t.category}</div>
                        <h3 className="font-semibold mt-2 leading-tight">{t.name}</h3>
                      </div>
                      <div className="text-right">
                        <div className="font-heading font-bold text-lg">₹{t.price}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Droplet size={12}/> {t.sample_type}</span>
                      <span className="inline-flex items-center gap-1"><Clock size={12}/> {t.report_hours}h {lang==="hi"?"में रिपोर्ट":"report"}</span>
                      {t.fasting_required && (
                        <span className="inline-flex items-center gap-1 text-amber-600 font-medium">⚡ Fasting</span>
                      )}
                      {t.home_collection && (
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><HomeIcon size={12}/> Home OK</span>
                      )}
                    </div>
                    <button onClick={() => toggleCart(t.test_id)}
                      data-testid={`pathology-toggle-${t.test_id}`}
                      className={`mt-3 w-full rounded-full py-2 text-sm font-medium transition ${inCart(t.test_id)
                        ? "bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-200"
                        : "bg-fuchsia-600 text-white hover:bg-fuchsia-700"}`}>
                      {inCart(t.test_id)
                        ? <span className="inline-flex items-center gap-1"><Minus size={14}/> {lang==="hi"?"हटाएं":"Remove"}</span>
                        : <span className="inline-flex items-center gap-1"><Plus size={14}/> {lang==="hi"?"बुक करें":"Book Now"}</span>}
                    </button>
                  </div>
                ))}
                {filtered.length === 0 && <div className="text-slate-400 p-6 col-span-2 text-center">{lang==="hi"?"कोई टेस्ट नहीं मिला":"No tests found"}</div>}
              </div>
            </div>

            {/* Summary sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl card-shadow p-5 sticky top-24" data-testid="pathology-summary">
                <h3 className="font-heading font-semibold text-lg">{lang==="hi"?"आपकी कार्ट":"Your Cart"} ({cart.length})</h3>
                {cart.length === 0 && (
                  <p className="text-sm text-slate-500 mt-3">{lang==="hi"?"अभी तक कोई टेस्ट नहीं जोड़ा।":"No tests added yet."}</p>
                )}
                <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                  {cartTests.map(t => (
                    <div key={t.test_id} className="flex items-start justify-between gap-2 text-sm">
                      <div className="flex-1">
                        <div className="font-medium leading-tight">{t.name}</div>
                        <div className="text-xs text-slate-400">₹{t.price}</div>
                      </div>
                      <button onClick={() => toggleCart(t.test_id)} className="text-slate-400 hover:text-red-500"><X size={14}/></button>
                    </div>
                  ))}
                </div>
                {cart.length > 0 && (
                  <>
                    <div className="border-t my-4"/>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-slate-500">Tests</span><span>₹{testsSubtotal}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Platform fee</span><span>₹{cfg.platform_fee}</span></div>
                    </div>
                    <div className="border-t my-3"/>
                    <div className="flex justify-between font-heading font-semibold text-lg">
                      <span>{lang==="hi"?"अनुमानित":"Estimate"}</span>
                      <span>₹{testsSubtotal + cfg.platform_fee}</span>
                    </div>
                    <button onClick={() => setStep(2)}
                      data-testid="pathology-next"
                      className="mt-4 w-full rounded-full py-3 bg-fuchsia-600 text-white font-medium hover:bg-fuchsia-700">
                      {lang==="hi"?"आगे बढ़ें":"Proceed"} <ChevronRight className="inline" size={16}/>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — patient + collection + payment */}
        {step === 2 && (
          <div className="mt-6 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {/* Collection method */}
              <div className="bg-white rounded-2xl card-shadow p-5">
                <h3 className="font-heading font-semibold">{lang==="hi"?"सैंपल कहाँ लें?":"Where to collect sample?"}</h3>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  {[
                    { key: "lab", icon: Building2, label: lang==="hi"?"लैब आकर":"Visit Lab", desc: lang==="hi"?"नज़दीकी सेंटर पर जाकर":"At nearest centre", extra: "" },
                    { key: "home", icon: HomeIcon, label: lang==="hi"?"होम कलेक्शन":"Home Collection", desc: lang==="hi"?"घर पर सैंपल":"Sample from home", extra: `+₹${cfg.home_collection_fee}` },
                  ].map(o => {
                    const Icon = o.icon;
                    const active = collection === o.key;
                    return (
                      <button key={o.key} onClick={() => setCollection(o.key)}
                        data-testid={`pathology-collection-${o.key}`}
                        className={`text-left p-4 rounded-2xl border-2 transition ${active ? "border-fuchsia-500 bg-fuchsia-50" : "border-slate-200 bg-white hover:border-fuchsia-300"}`}>
                        <div className="flex items-center justify-between">
                          <Icon className={active ? "text-fuchsia-600" : "text-slate-400"} size={20}/>
                          {o.extra && <span className="chip bg-amber-100 text-amber-700 text-[10px]">{o.extra}</span>}
                        </div>
                        <div className="mt-2 font-semibold">{o.label}</div>
                        <div className="text-xs text-slate-500">{o.desc}</div>
                      </button>
                    );
                  })}
                </div>
                {collection === "home" && (
                  <div className="mt-4">
                    <label className="text-sm font-medium">{lang==="hi"?"कलेक्शन का पूरा पता":"Full collection address"} *</label>
                    <textarea rows={2} value={collectionAddr}
                      onChange={e => setCollectionAddr(e.target.value)}
                      data-testid="pathology-collection-address"
                      placeholder={lang==="hi"?"मकान नंबर, गली, मुहल्ला, लैंडमार्क, पिन":"House no, street, locality, landmark, PIN"}
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-fuchsia-500"/>
                  </div>
                )}
                {anyFasting && (
                  <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                    ⚡ {lang==="hi"?"आपने ऐसे टेस्ट चुने हैं जिनके लिए 8-12 घंटे का fasting ज़रूरी है।":"Some selected tests require 8–12 hours of fasting before sample collection."}
                  </div>
                )}
              </div>

              {/* Date / time */}
              <div className="bg-white rounded-2xl card-shadow p-5">
                <h3 className="font-heading font-semibold">{lang==="hi"?"तारीख और समय":"Date & Time"}</h3>
                <div className="mt-3 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2"><CalendarDays size={16}/>{lang==="hi"?"तारीख":"Date"}</label>
                    <input type="date" value={slotDate}
                      min={new Date().toISOString().slice(0,10)}
                      onChange={e => setSlotDate(e.target.value)}
                      data-testid="pathology-date"
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-fuchsia-500"/>
                  </div>
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2"><Clock size={16}/>{lang==="hi"?"समय":"Time"}</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {SLOT_TIMES.map(s => (
                        <button key={s} onClick={() => setSlotTime(s)}
                          data-testid={`pathology-slot-${s}`}
                          className={`px-3 py-2 rounded-full text-sm border ${slotTime === s ? "bg-fuchsia-600 text-white border-fuchsia-600" : "bg-white border-slate-200 hover:border-fuchsia-500"}`}>{s}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Patient details */}
              <div className="bg-white rounded-2xl card-shadow p-5">
                <h3 className="font-heading font-semibold">{lang==="hi"?"मरीज़ की जानकारी":"Patient Details"}</h3>
                <div className="mt-3 grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">{lang==="hi"?"नाम":"Name"} *</label>
                    <input value={patient.name} onChange={e => setPatient({ ...patient, name: e.target.value })}
                      data-testid="pathology-patient-name"
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-fuchsia-500"/>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{lang==="hi"?"उम्र":"Age"} *</label>
                    <input type="number" value={patient.age} onChange={e => setPatient({ ...patient, age: +e.target.value })}
                      data-testid="pathology-patient-age"
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-fuchsia-500"/>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{lang==="hi"?"लिंग":"Gender"}</label>
                    <select value={patient.gender} onChange={e => setPatient({ ...patient, gender: e.target.value })}
                      data-testid="pathology-patient-gender"
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-fuchsia-500">
                      <option value="male">{lang==="hi"?"पुरुष":"Male"}</option>
                      <option value="female">{lang==="hi"?"महिला":"Female"}</option>
                      <option value="other">{lang==="hi"?"अन्य":"Other"}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">{lang==="hi"?"मोबाइल नंबर":"Mobile"} *</label>
                    <input inputMode="numeric" maxLength={10} value={patient.phone}
                      onChange={e => setPatient({ ...patient, phone: e.target.value.replace(/\D/g,"").slice(0,10) })}
                      data-testid="pathology-patient-phone"
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-fuchsia-500 font-mono"/>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium">{lang==="hi"?"रेफ़र करने वाले डॉक्टर (वैकल्पिक)":"Referring Doctor (optional)"}</label>
                    <input value={doctorName} onChange={e => setDoctorName(e.target.value)}
                      data-testid="pathology-doctor"
                      placeholder="Dr. …"
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-fuchsia-500"/>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium">{lang==="hi"?"नोट्स (वैकल्पिक)":"Notes (optional)"}</label>
                    <textarea rows={2} value={patient.notes || ""} onChange={e => setPatient({ ...patient, notes: e.target.value })}
                      data-testid="pathology-notes"
                      className="mt-1 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-fuchsia-500"/>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button onClick={() => setStep(1)} data-testid="pathology-back-1" className="btn-outline">
                  <ArrowLeft size={16}/> {lang==="hi"?"पीछे":"Back"}
                </button>
                <button onClick={() => placeBooking()} disabled={!canProceed2() || placing}
                  data-testid="pathology-pay"
                  className={`rounded-full px-6 py-3 bg-fuchsia-600 text-white font-medium hover:bg-fuchsia-700 disabled:opacity-50 disabled:pointer-events-none`}>
                  {placing ? "…" : (lang==="hi"?"भुगतान करें":"Pay")} <IndianRupee size={16} className="inline"/> {total}
                </button>
              </div>
            </div>

            {/* Sticky summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl card-shadow p-5 sticky top-24">
                <h3 className="font-heading font-semibold text-lg">{lang==="hi"?"सारांश":"Summary"}</h3>
                <div className="mt-3 space-y-1 text-sm max-h-40 overflow-y-auto">
                  {cartTests.map(t => (
                    <div key={t.test_id} className="flex justify-between">
                      <span className="text-slate-600 truncate pr-2">{t.name}</span>
                      <span>₹{t.price}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t my-3"/>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Tests subtotal</span><span>₹{testsSubtotal}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Platform fee</span><span>₹{cfg.platform_fee}</span></div>
                  {collection === "home" && <div className="flex justify-between"><span className="text-slate-500">Home collection</span><span>₹{collectionFee}</span></div>}
                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-emerald-600 font-medium">
                      <span>Coupon</span><span>−₹{couponDiscount}</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t">
                  {couponDiscount > 0 ? (
                    <button onClick={() => { setCouponCode(""); setCouponDiscount(0); }} className="text-xs text-red-500 hover:underline">Remove coupon</button>
                  ) : (
                    <div className="flex gap-2">
                      <input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        placeholder={lang==="hi"?"कूपन":"Coupon"}
                        data-testid="pathology-coupon"
                        className="flex-1 rounded-full bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs outline-none focus:border-fuchsia-500"/>
                      <button onClick={applyCoupon} data-testid="pathology-coupon-apply" className="px-3 py-1.5 rounded-full bg-fuchsia-600 text-white text-xs font-medium hover:bg-fuchsia-700">Apply</button>
                    </div>
                  )}
                  {couponErr && <p className="text-xs text-red-500 mt-1">{couponErr}</p>}
                </div>

                <div className="border-t my-3"/>
                <div className="flex justify-between font-heading font-semibold text-lg">
                  <span>{lang==="hi"?"कुल":"Total"}</span><span>₹{total}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — confirmation */}
        {step === 3 && confirmed && (
          <div className="mt-6 max-w-2xl mx-auto bg-white rounded-3xl card-shadow p-8 text-center" data-testid="pathology-confirmation">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><CheckCircle2 size={30}/></div>
            <h2 className="font-heading text-2xl font-bold mt-4">{lang==="hi"?"बुकिंग confirm हो गई!":"Booking Confirmed!"}</h2>
            <p className="text-slate-500 text-sm mt-1">{lang==="hi"?"रिपोर्ट तैयार होते ही आपको SMS/Email मिलेगा।":"You'll receive SMS/Email when reports are ready."}</p>

            <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-5 text-left space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Booking ID</span><span className="font-mono font-semibold">{confirmed.booking_id}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Token</span><span className="font-mono">{confirmed.token_number}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Patient</span><span>{confirmed.patient.name} ({confirmed.patient.age})</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Phone</span><span>{confirmed.patient.phone}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Slot</span><span>{confirmed.slot_date} · {confirmed.slot_time}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Collection</span><span className="uppercase">{confirmed.collection_method}</span></div>
              {confirmed.collection_address && <div className="flex justify-between gap-4"><span className="text-slate-500 shrink-0">Address</span><span className="text-right">{confirmed.collection_address}</span></div>}
              <div className="border-t pt-2">
                <div className="text-slate-500 mb-1">Tests</div>
                {(confirmed.pathology_tests || []).map(t => (
                  <div key={t.test_id} className="flex justify-between">
                    <span>{t.name}</span><span>₹{t.price}</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-2 flex justify-between font-heading font-semibold">
                <span>Total Paid</span><span>₹{confirmed.total_amount}</span>
              </div>
              <div className="flex justify-between"><span className="text-slate-500">Payment</span>
                <span className={`chip ${confirmed.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {confirmed.payment_status || "pending"}
                </span>
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-center">
              <button onClick={() => navigate("/dashboard")} className="btn-primary">{lang==="hi"?"मेरी बुकिंग्स":"My Bookings"}</button>
              <button onClick={() => { setStep(1); setCart([]); setConfirmed(null); setOtpToken(null); }} className="btn-outline">{lang==="hi"?"नई बुकिंग":"Book Another"}</button>
            </div>
          </div>
        )}
      </div>

      {payOpen && orderInfo && confirmed && (
        <UpiQrPayModal open={payOpen} onClose={() => setPayOpen(false)}
          order={orderInfo} lang={lang}
          description={`Pathology · ${confirmed.booking_id}`}/>
      )}

      <OtpModal open={otpOpen} onClose={() => setOtpOpen(false)}
        phone={patient.phone} lang={lang}
        onVerified={(token, phone) => {
          setOtpToken(token);
          setPatient(p => ({ ...p, phone }));
          setOtpOpen(false);
          placeBooking(token);
        }}/>

      <WhatsAppFab />
      <Footer />
    </div>
  );
}
